import classNames from "classnames";
import { useRef, useEffect, useState } from "react";
import styles from "./styles.module.scss";

interface InputProps {
  id?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  icon?: React.ReactNode;
  label?: string;
  className?: string;
  size?: "small" | "default" | "large";
  paddingSize?: "normal" | "dense";
  type?: "text" | "number";
  style?: React.CSSProperties;
  suffix?: string;
}

export const Input: React.FC<InputProps> = ({
  id,
  value,
  placeholder,
  onChange,
  icon,
  label,
  className,
  size = "default",
  paddingSize = "normal",
  type = "text",
  style,
  suffix,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputWidth, setInputWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);

  useEffect(() => {
    if (inputRef.current && value) {
      const span = document.createElement("span");
      span.style.visibility = "hidden";
      span.style.position = "absolute";
      span.style.fontSize = "14px";
      span.style.fontFamily = "Roboto";
      span.style.letterSpacing = "-0.2px";
      span.style.whiteSpace = "pre";
      span.innerText = value;

      document.body.appendChild(span);
      const width = span.offsetWidth;
      document.body.removeChild(span);

      setTextWidth(width);
    } else {
      setTextWidth(0);
    }
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      const inputContainerWidth =
        inputRef.current.parentElement?.offsetWidth || 0;
      setInputWidth(inputContainerWidth);
    }
  }, []);

  const getPositionStyle = () => {
    if (!value || value.length === 0) {
      return { left: 50, opacity: 0.5 };
    }

    const leftPosition = Math.min(textWidth + 55, inputWidth - 70);
    return { left: leftPosition, opacity: 1 };
  };

  return (
    <div
      className={classNames(
        styles.container,
        className,
        styles[size],
        styles[paddingSize],
      )}
      style={style}
    >
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}

      <div className={styles.input}>
        <span className={styles.icon}>{icon}</span>

        <input
          id={id}
          ref={inputRef}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange?.(e.target.value)}
          className={classNames(type === "number" ? styles.noSpin : "")}
        />
        {suffix && value && (
          <span className={styles.dynamicSuffix} style={getPositionStyle()}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};
